import { call, put, SagaReturnType, select } from 'redux-saga/effects';

import * as api from 'src/api/api';
import {
  AddCommunityMemberTransaction,
  CreateUserTransaction,
  RemoveCommunityMemberTransaction,
  RemoveUserTransaction,
  UpdateUserTransaction,
} from 'src/types/admin';
import { Role, User, UserRequestPayload } from 'src/types/auth';
import { CollectionRole } from 'src/types/collection';
import { CRUD } from 'src/types/core';
import { Community } from 'src/types/forum';
import { CatalogRole, OrganizationRole } from 'src/types/organization';
import { getCommunitiesPermission, hasAnyPermission } from 'src/util/user';
import authSelectors from '../auth/auth-selectors';
import { editUserSuccess } from '../auth/auth-slice';
import adminSelectors from './admin-selectors';
import {
  getCommunityMembersSuccess,
  userTransactionFailure,
  userTransactionSuccess,
} from './admin-slice';

export function* processOrganizationRoleUpdates(
  transaction: UpdateUserTransaction
) {
  const organizationId: ReturnType<typeof adminSelectors.getOrganizationId> =
    yield select(adminSelectors.getOrganizationId);
  if (organizationId == null) {
    yield put(
      userTransactionFailure({
        transaction,
        error: {
          status: 'fail',
          message: 'Need an organization id',
        },
      })
    );
  } else {
    const { id: userId, roles } = transaction.data;

    if (roles) {
      const role_types: string[] = roles.map((r) => r.role_type);
      if (transaction.crud === CRUD.post && role_types.length > 0) {
        yield call(api.postUserRoles, userId, { role_types, organizationId });
      }
      if (transaction.crud === CRUD.put) {
        yield call(api.putUserRoles, userId, { role_types, organizationId });
      }
      if (transaction.crud === CRUD.delete && role_types.length > 0) {
        yield call(api.deleteUserRoles, userId, { role_types, organizationId });
      }

      const updatedUser: SagaReturnType<typeof api.getUserInOrganizationById> =
        yield call(api.getUserInOrganizationById, organizationId, userId);

      // Put updated user into array of all users
      yield put(userTransactionSuccess({ updatedUser, transaction }));
      // Put updated user into auth in case it is current user
      yield put(editUserSuccess({ updatedUser }));
    }
  }
}

export function* processCommunityRoleUpdates(
  transaction: UpdateUserTransaction
) {
  const organizationId: ReturnType<typeof adminSelectors.getOrganizationId> =
    yield select(adminSelectors.getOrganizationId);
  if (organizationId == null) {
    yield put(
      userTransactionFailure({
        transaction,
        error: {
          status: 'fail',
          message: 'Need an organization id',
        },
      })
    );
  } else {
    const targetCommunityId =
      transaction.data.communityId ||
      (
        (yield select(adminSelectors.getActiveCommunity)) as ReturnType<
          typeof adminSelectors.getActiveCommunity
        >
      )?.id;
    if (!targetCommunityId) {
      yield put(
        userTransactionFailure({
          transaction,
          error: {
            status: 'fail',
            message: 'Need a community id',
          },
        })
      );
    } else {
      const { id: userId, roles } = transaction.data;

      if (roles) {
        const role_types: string[] = roles.map((r) => r.role_type);
        if (transaction.crud === CRUD.post && role_types.length > 0) {
          yield call(api.postUserRoles, userId, {
            role_types,
            communityId: targetCommunityId,
          });
        }
        if (transaction.crud === CRUD.put) {
          yield call(api.putUserRoles, userId, {
            role_types,
            communityId: targetCommunityId,
          });
        }
        if (transaction.crud === CRUD.delete && role_types.length > 0) {
          yield call(api.deleteUserRoles, userId, {
            role_types,
            communityId: targetCommunityId,
          });
        }

        const updatedCommunityMembers: SagaReturnType<
          typeof api.getCommunityMembers
        > = yield call(api.getCommunityMembers, {
          communityId: targetCommunityId,
        });

        const updatedUser: SagaReturnType<
          typeof api.getUserInOrganizationById
        > = yield call(api.getUserInOrganizationById, organizationId, userId);

        // Update community users in state
        yield put(getCommunityMembersSuccess(updatedCommunityMembers));
        // Put updated user into array of all users
        yield put(userTransactionSuccess({ updatedUser, transaction }));
      }
    }
  }
}

export function* processCatalogRoleUpdates(transaction: UpdateUserTransaction) {
  const { id: userId, roles, catalogId, organization_id } = transaction.data;
  const role_types = roles?.map((r) => r.role_type);
  let organizationId: ReturnType<typeof adminSelectors.getOrganizationId> =
    organization_id;
  if (!organizationId) {
    organizationId = yield select(adminSelectors.getOrganizationId);
  }

  if (!(catalogId && organizationId && userId)) {
    yield put(
      userTransactionFailure({
        transaction,
        error: {
          status: 'fail',
          message: 'Need a catalog, organization, and user id',
        },
      })
    );
  } else {
    if (role_types) {
      if (transaction.crud === CRUD.put && role_types.length > 0) {
        yield call(api.putUserRoles, userId, {
          role_types,
          catalogId,
        });
      }

      if (transaction.crud === CRUD.delete && role_types.length > 0) {
        yield call(api.deleteUserRoles, userId, {
          role_types,
          catalogId,
        });
      }

      const updatedUser: SagaReturnType<typeof api.getUserInOrganizationById> =
        yield call(api.getUserInOrganizationById, organizationId, userId);

      // Put updated user into array of all users
      yield put(userTransactionSuccess({ updatedUser, transaction }));
      // Put updated user into auth in case it is current user
      yield put(editUserSuccess({ updatedUser }));
    }
  }
}

export function* processCollectionRoleUpdates(
  transaction: UpdateUserTransaction
) {
  const organizationId: ReturnType<typeof adminSelectors.getOrganizationId> =
    yield select(adminSelectors.getOrganizationId);
  const { id: userId, roles, collectionId } = transaction.data;
  const role_types = roles?.map((r) => r.role_type);

  if (collectionId == null || organizationId == null) {
    yield put(
      userTransactionFailure({
        transaction,
        error: {
          status: 'fail',
          message: 'Need a collection or organization id',
        },
      })
    );
  } else {
    // NOTE we don't check for length here because we can pass an empty array to clear out roles
    // just check to make sure it is defined
    if (role_types) {
      if (transaction.crud === CRUD.post) {
        yield call(api.postUserRoles, userId, { role_types, collectionId });
      }
      if (transaction.crud === CRUD.put) {
        yield call(api.putUserRoles, userId, { role_types, collectionId });
      }
      if (transaction.crud === CRUD.delete) {
        yield call(api.deleteUserRoles, userId, { role_types, collectionId });
      }

      const updatedUser: SagaReturnType<typeof api.getUserInOrganizationById> =
        yield call(api.getUserInOrganizationById, organizationId, userId);

      // Put updated user into array of all users
      yield put(userTransactionSuccess({ updatedUser, transaction }));
      // Put updated user into auth in case it is current user
      yield put(editUserSuccess({ updatedUser }));
    }
  }
}

export function* processUserUpdates(transaction: UpdateUserTransaction) {
  const updatedUser: SagaReturnType<typeof api.putUserEdit> = yield call(
    api.putUserEdit,
    transaction.data
  );
  // Put updated user into array of all users
  yield put(userTransactionSuccess({ updatedUser, transaction }));
  // Put updated user into auth in case it is currnt user
  yield put(editUserSuccess({ updatedUser }));
}

export function* processAddUser(transaction: CreateUserTransaction) {
  const organizationId: ReturnType<typeof adminSelectors.getOrganizationId> =
    yield select(adminSelectors.getOrganizationId);

  if (organizationId != null) {
    const { org_role, collections, communities, catalogs, ...userInfo } =
      transaction.data;

    const roles = rolesFromCreateUserTransaction(transaction, organizationId);

    const newUserRequest: Partial<UserRequestPayload> = {
      ...userInfo,
      roles,
    };

    const createdUser: SagaReturnType<typeof api.postUserCreation> = yield call(
      api.postUserCreation,
      newUserRequest,
      organizationId
    );

    // Put newly created user into array of all users
    yield put(
      userTransactionSuccess({
        updatedUser: createdUser,
        transaction: transaction,
      })
    );
  }
}

function rolesFromCreateUserTransaction(
  transaction: CreateUserTransaction,
  organizationId: number
): Role[] {
  const { org_role, collections, communities, catalogs } = transaction.data;

  const roles: Role[] = [
    ...collections.flatMap((c) => {
      const collectionRoles = [
        {
          collection_id: c.collection.id,
          role_type: c.role,
        },
      ];

      if (c.canUploadConversations) {
        collectionRoles.push({
          collection_id: c.collection.id,
          role_type: CollectionRole.host,
        });
      }

      return collectionRoles;
    }),
    ...communities.map((c) => {
      return {
        community_id: c.community.id,
        role_type: c.role,
      };
    }),
    ...catalogs.map((c) => {
      // should only get here if the sensemaker boolean is true (the form filters out false values)
      return {
        catalog_id: c.catalog.id,
        role_type: CatalogRole.sensemaker,
      };
    }),
  ];

  // There isn't really an organization member role because it doesn't tie you to any permissions
  // so only set the org role if it's something else
  if (org_role.toLowerCase() !== OrganizationRole.member.toLowerCase()) {
    roles.push({
      organization_id: organizationId,
      role_type: org_role,
    });
  }

  return roles;
}

export function* processLinkUser(
  transaction: CreateUserTransaction,
  userId: number
) {
  const organizationId: ReturnType<typeof adminSelectors.getOrganizationId> =
    yield select(adminSelectors.getOrganizationId);

  if (organizationId != null) {
    // Attempt to link user
    const roles = rolesFromCreateUserTransaction(transaction, organizationId);

    yield call(
      api.postLinkUserToOrganization,
      organizationId,
      transaction.data.email,
      userId,
      roles
    );

    const linkedUser: SagaReturnType<typeof api.getUserInOrganizationById> =
      yield call(api.getUserInOrganizationById, organizationId, userId);
    // Put updated user into array of all users
    yield put(userTransactionSuccess({ updatedUser: linkedUser, transaction }));
  }
}

export function* processRemoveUser(transaction: RemoveUserTransaction) {
  const organizationId: ReturnType<typeof adminSelectors.getOrganizationId> =
    yield select(adminSelectors.getOrganizationId);
  if (organizationId != null) {
    yield call(
      api.removeUserFromOrganization,
      transaction.data.id,
      organizationId
    );
    // Put updated user into array of all users
    yield put(
      userTransactionSuccess({
        updatedUser: { id: transaction.data.id },
        transaction,
      })
    );
  }
}

export function* processRemoveCommunityMember(
  transaction: RemoveCommunityMemberTransaction
) {
  const organizationId: ReturnType<typeof adminSelectors.getOrganizationId> =
    yield select(adminSelectors.getOrganizationId);

  const community: ReturnType<typeof adminSelectors.getActiveCommunity> =
    yield select(adminSelectors.getActiveCommunity);

  if (community != null && organizationId != null) {
    yield call(api.putUserRoles, transaction.data.id, {
      role_types: [],
      communityId: transaction.data.communityId || community.id,
    });

    const updatedUser: SagaReturnType<typeof api.getUserInOrganizationById> =
      yield call(
        api.getUserInOrganizationById,
        organizationId,
        transaction.data.id
      );

    // Put updated user into array of all users and update state.admin.communityMembers
    yield put(
      userTransactionSuccess({
        updatedUser,
        transaction,
      })
    );
  }
}

export function* processAddCommunityMember(
  transaction: AddCommunityMemberTransaction
) {
  const organizationId: ReturnType<typeof adminSelectors.getOrganizationId> =
    yield select(adminSelectors.getOrganizationId);

  const community: ReturnType<typeof adminSelectors.getActiveCommunity> =
    yield select(adminSelectors.getActiveCommunity);

  if (community != null && organizationId != null) {
    yield call(api.putUserRoles, transaction.data.id, {
      role_types: transaction.data.roles.map((r) => r.role_type.toLowerCase()),
      communityId: transaction.data.communityId || community.id,
    });

    const updatedUser: SagaReturnType<typeof api.getUserInOrganizationById> =
      yield call(
        api.getUserInOrganizationById,
        organizationId,
        transaction.data.id
      );

    // Put updated user into array of all users and update state.admin.communityMembers
    yield put(
      userTransactionSuccess({
        updatedUser,
        transaction,
      })
    );
  }
}

export function* getDefaultCommunity(communities: Community[]) {
  const currentUser: ReturnType<typeof authSelectors.getUser> = yield select(
    authSelectors.getUser
  );

  let defaultCommunity: Community | undefined;

  communities.forEach((community) => {
    if (canAccessAdminCommunity(community.id, currentUser)) {
      defaultCommunity = community;
    }
  });

  return defaultCommunity;
}

export const canAccessAdminCommunity = (
  communityId: Community['id'],
  currentUser: User
) => {
  const perms = [getCommunitiesPermission('read', communityId)];
  return hasAnyPermission(currentUser, perms);
};
